import React, { useRef, useState } from 'react'
import {
   Alert,
   Box,
   Button,
   Checkbox,
   Chip,
   CircularProgress,
   FormControlLabel,
   Paper,
   TextField,
   Typography,
} from '@mui/material'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import MemoryIcon from '@mui/icons-material/Memory'
import axios from '../api/axios'

const MAX_CUW_BYTES = 32 * 1024 * 1024
const PASSWORD_ALPHABET =
   'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789'

const parseDownloadName = (contentDisposition, fallback) => {
   const value = String(contentDisposition || '')

   const utf8 = value.match(/filename\*=UTF-8''([^;]+)/i)
   if (utf8?.[1]) {
      try {
         return decodeURIComponent(utf8[1])
      } catch (_) {}
   }

   const quoted = value.match(/filename="([^"]+)"/i)
   if (quoted?.[1]) return quoted[1]

   const plain = value.match(/filename=([^;]+)/i)
   if (plain?.[1]) return plain[1].trim()

   return fallback
}

const parseBlobError = async (error) => {
   const blob = error?.response?.data

   if (blob instanceof Blob) {
      try {
         const text = await blob.text()
         const json = JSON.parse(text)
         return json
      } catch (_) {}
   }

   return error?.response?.data || null
}

const generatePassword = () => {
   const bytes = new Uint32Array(16)
   window.crypto.getRandomValues(bytes)
   const raw = Array.from(bytes, (n) =>
      PASSWORD_ALPHABET.charAt(n % PASSWORD_ALPHABET.length)
   ).join('')
   return raw.match(/.{1,4}/g).join('-')
}

export default function AdminCuwDecoder() {
   const inputRef = useRef(null)
   const [file, setFile] = useState(null)
   const [loading, setLoading] = useState(false)
   const [error, setError] = useState('')
   const [lastResult, setLastResult] = useState(null)
   const [passwordProtect, setPasswordProtect] = useState(false)
   const [zipPassword, setZipPassword] = useState('')
   const [copyMessage, setCopyMessage] = useState('')

   const chooseFile = (selected) => {
      setError('')
      setLastResult(null)
      setCopyMessage('')

      if (!selected) {
         setFile(null)
         return
      }

      if (!selected.name.toLowerCase().endsWith('.cuw')) {
         setFile(null)
         setError('Only .cuw files are accepted.')
         return
      }

      if (selected.size > MAX_CUW_BYTES) {
         setFile(null)
         setError('CUW file is larger than 32 MiB.')
         return
      }

      setFile(selected)
   }

   const togglePassword = (checked) => {
      setPasswordProtect(checked)
      setLastResult(null)
      setCopyMessage('')
      if (checked && !zipPassword) setZipPassword(generatePassword())
   }

   const makePassword = () => {
      setZipPassword(generatePassword())
      setLastResult(null)
      setCopyMessage('')
   }

   const copyPassword = async (value) => {
      try {
         await navigator.clipboard.writeText(value)
         setCopyMessage('Password copied.')
      } catch (_) {
         setCopyMessage('Could not copy automatically. Select and copy the password.')
      }
   }

   const decode = async () => {
      if (!file || loading) return

      if (passwordProtect && !zipPassword) {
         setError('Enter or generate a ZIP password first.')
         return
      }

      setLoading(true)
      setError('')
      setLastResult(null)
      setCopyMessage('')

      try {
         const form = new FormData()
         form.append('file', file)
         if (passwordProtect) form.append('zipPassword', zipPassword)

         const response = await axios.post(
            'admin/toolbox/cuw-decode',
            form,
            { responseType: 'blob' }
         )

         const status = response.headers['x-cuw-status'] || ''
         const family = response.headers['x-cuw-family'] || ''
         const binCount = Number(response.headers['x-cuw-bin-count'] || 1)
         const encrypted = response.headers['x-cuw-zip-encrypted'] === '1'
         const fallback = `${file.name.replace(/\.cuw$/i, '')}_BIN.zip`

         const filename = parseDownloadName(
            response.headers['content-disposition'],
            fallback
         )

         const blob = new Blob([response.data], {
            type: response.headers['content-type'] || 'application/zip',
         })
         const url = URL.createObjectURL(blob)
         const link = document.createElement('a')
         link.href = url
         link.download = filename
         document.body.appendChild(link)
         link.click()
         link.remove()
         URL.revokeObjectURL(url)

         setLastResult({
            status,
            family,
            binCount,
            filename,
            encrypted,
            password: encrypted ? zipPassword : '',
         })
      } catch (err) {
         const payload = await parseBlobError(err)
         const message =
            payload?.message ||
            err?.message ||
            'CUW decode failed.'

         const details = [payload?.status, payload?.family]
            .filter(Boolean)
            .join(' / ')

         setError(details ? `${message} (${details})` : message)
      } finally {
         setLoading(false)
      }
   }

   return (
      <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 900 }}>
         <Paper
            elevation={0}
            sx={{
               border: '1px solid #E4E7EC',
               borderRadius: 2,
               overflow: 'hidden',
            }}
         >
            <Box sx={{ p: { xs: 2.5, md: 3 } }}>
               <Box
                  sx={{
                     display: 'flex',
                     alignItems: 'center',
                     gap: 1.5,
                     mb: 1,
                  }}
               >
                  <MemoryIcon sx={{ color: '#163A5F', fontSize: 32 }} />
                  <Box>
                     <Typography
                        sx={{
                           color: '#172B4D',
                           fontSize: '22px',
                           fontWeight: 800,
                        }}
                     >
                        CUW → BIN Decoder
                     </Typography>
                     <Typography sx={{ color: '#667085', fontSize: '14px' }}>
                        Internal admin tool · V1.5.8 production decoder
                     </Typography>
                  </Box>
               </Box>

               <Typography
                  sx={{ color: '#475467', fontSize: '14px', mt: 2, mb: 2.5 }}
               >
                  Upload a Toyota/Lexus CUW file. Original CUW files are archived
                  privately for internal use. Final BIN output is always returned
                  as a ZIP and is not permanently stored. ZIP files can optionally
                  be protected with AES-256 encryption.
               </Typography>

               <input
                  ref={inputRef}
                  type="file"
                  accept=".cuw"
                  hidden
                  onChange={(event) => chooseFile(event.target.files?.[0])}
               />

               <Box
                  sx={{
                     border: '1px dashed #B8C2CC',
                     borderRadius: 2,
                     p: 2.5,
                     backgroundColor: '#FAFBFC',
                  }}
               >
                  <Box
                     sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'stretch', sm: 'center' },
                        gap: 1.5,
                     }}
                  >
                     <Button
                        variant="outlined"
                        startIcon={<UploadFileIcon />}
                        onClick={() => inputRef.current?.click()}
                        disabled={loading}
                     >
                        Select CUW
                     </Button>

                     <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography
                           sx={{
                              color: file ? '#172B4D' : '#98A2B3',
                              fontSize: '14px',
                              fontWeight: file ? 700 : 400,
                              overflowWrap: 'anywhere',
                           }}
                        >
                           {file ? file.name : 'No file selected'}
                        </Typography>
                        {file && (
                           <Typography sx={{ color: '#98A2B3', fontSize: '12px' }}>
                              {(file.size / 1024 / 1024).toFixed(2)} MiB
                           </Typography>
                        )}
                     </Box>

                     <Button
                        variant="contained"
                        onClick={decode}
                        disabled={!file || loading}
                        sx={{ minWidth: 145 }}
                     >
                        {loading ? (
                           <CircularProgress size={22} color="inherit" />
                        ) : (
                           'Decode to ZIP'
                        )}
                     </Button>
                  </Box>

                  <Box
                     sx={{
                        mt: 2,
                        pt: 2,
                        borderTop: '1px solid #E4E7EC',
                     }}
                  >
                     <FormControlLabel
                        control={
                           <Checkbox
                              checked={passwordProtect}
                              onChange={(event) => togglePassword(event.target.checked)}
                              disabled={loading}
                           />
                        }
                        label="Password protect ZIP (AES-256)"
                     />

                     {passwordProtect && (
                        <Box
                           sx={{
                              mt: 1,
                              display: 'flex',
                              flexDirection: { xs: 'column', sm: 'row' },
                              gap: 1,
                              alignItems: { xs: 'stretch', sm: 'center' },
                           }}
                        >
                           <TextField
                              label="ZIP password"
                              value={zipPassword}
                              onChange={(event) => {
                                 setZipPassword(event.target.value)
                                 setLastResult(null)
                                 setCopyMessage('')
                              }}
                              disabled={loading}
                              size="small"
                              inputProps={{ maxLength: 128 }}
                              sx={{ flex: 1 }}
                           />
                           <Button
                              variant="outlined"
                              onClick={makePassword}
                              disabled={loading}
                           >
                              Generate
                           </Button>
                        </Box>
                     )}

                     {passwordProtect && (
                        <Typography sx={{ color: '#667085', fontSize: '12px', mt: 1 }}>
                           The password is used only for this download and is not
                           stored in the CUW archive or decoder metadata.
                        </Typography>
                     )}
                  </Box>
               </Box>

               {error && (
                  <Alert severity="error" sx={{ mt: 2.5 }}>
                     {error}
                  </Alert>
               )}

               {lastResult && (
                  <Alert severity="success" sx={{ mt: 2.5 }}>
                     <Box
                        sx={{
                           display: 'flex',
                           flexWrap: 'wrap',
                           alignItems: 'center',
                           gap: 1,
                        }}
                     >
                        <Typography sx={{ fontWeight: 700 }}>
                           Decode complete: {lastResult.filename}
                        </Typography>
                        {lastResult.status && (
                           <Chip size="small" label={lastResult.status} />
                        )}
                        {lastResult.family && (
                           <Chip size="small" label={lastResult.family} />
                        )}
                        <Chip
                           size="small"
                           label={`${lastResult.binCount} BIN${
                              lastResult.binCount === 1 ? '' : 's'
                           }`}
                        />
                        {lastResult.encrypted && (
                           <Chip size="small" label="AES-256 ZIP" />
                        )}
                     </Box>

                     {lastResult.encrypted && lastResult.password && (
                        <Box
                           sx={{
                              mt: 2,
                              p: 1.5,
                              borderRadius: 1.5,
                              backgroundColor: 'rgba(255,255,255,0.65)',
                           }}
                        >
                           <Typography sx={{ fontSize: '12px', color: '#475467' }}>
                              ZIP password — copy this before leaving the page:
                           </Typography>
                           <Box
                              sx={{
                                 mt: 0.5,
                                 display: 'flex',
                                 flexDirection: { xs: 'column', sm: 'row' },
                                 gap: 1,
                                 alignItems: { xs: 'stretch', sm: 'center' },
                              }}
                           >
                              <Typography
                                 component="code"
                                 sx={{
                                    fontSize: '16px',
                                    fontWeight: 800,
                                    color: '#172B4D',
                                    overflowWrap: 'anywhere',
                                    flex: 1,
                                 }}
                              >
                                 {lastResult.password}
                              </Typography>
                              <Button
                                 variant="outlined"
                                 size="small"
                                 onClick={() => copyPassword(lastResult.password)}
                              >
                                 Copy password
                              </Button>
                           </Box>
                           {copyMessage && (
                              <Typography sx={{ mt: 0.75, fontSize: '12px', color: '#667085' }}>
                                 {copyMessage}
                              </Typography>
                           )}
                        </Box>
                     )}
                  </Alert>
               )}
            </Box>
         </Paper>
      </Box>
   )
}
